import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { UserSessionService } from '../../services/user-session.service';
import { AuthService } from '../../services/auth.service';
import { LogService } from '../../services/log.service';
import { NavigationEnd, Router } from '@angular/router';
import { ControltaskService } from '../../services/controltask.service';
import { ControlhelpService } from '../../services/controlhelp.service';
import { Subscription } from 'rxjs';
import { LanguageService } from '../../services/language.service';

interface MenuUserInfo {
  tenantName: string;
  lastAccessLabel: string;
  roleName: string;
  departmentName: string;
  displayName: string;
  initials: string;
}

interface ModuleSearchEntry {
  key: string;
  label: string;
  route: string;
  icon: string;
  aliases: string[];
}

type FieldsLabTab = 'inputs' | 'select' | 'textarea' | 'date' | 'search' | 'buttons' | 'icons' | 'deploy';

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss'
})
export class MenuComponent implements OnInit, OnDestroy, AfterViewInit {

    isClosed: boolean = true;
    private openedByHover = false;
    mobileMenuOpen = false;
    isMobileViewport = false;
    userInfo: MenuUserInfo | null = null;
    intranetOpen = false;
    allowedModules = new Set<string>();
    modulePermissions = new Map<string, { can_view: boolean; can_create: boolean; can_manage: boolean }>();
    hasModuleList = false;
    menuScrollable = false;
    moduleSearchTerm = '';
    moduleSearchResults: ModuleSearchEntry[] = [];
    moduleSearchOpen = false;
    avatarMenuOpen = false;
    isMasterProfile = false;
    isMasterOnlyProfile = false;
    isMainTenant = false;
    private avatarMenuTarget: HTMLElement | null = null;
    @ViewChild('moduleSearchRef') moduleSearchRef?: ElementRef<HTMLElement>;
    weatherCity = 'São Paulo, Brasil';
    weatherIcon = 'cloud';
    weatherTempLabel = '--°C';
    weatherRangeLabel = 'Hoje: --°C / --°C';
    languageLabel = 'PT';
    todayTasksCount = 0;
    controlhelpHasUpdates = false;
    controlhelpDepartmentIds: number[] = [];
    showTaskModal = false;
    showFieldsLabModal = false;
    fieldsLabForm = {
      inputValue: '',
      selectValue: '',
      multiSelectValues: [] as string[],
      dateValue: '',
      dateTimeValue: '',
      searchValue: '',
      tableSearchValue: '',
      textareaValue: '',
      checkboxValue: true,
      toggleValue: true
    };
    fieldsLabDatePickerValue: Date | null = null;
    fieldsLabDateTimePickerValue: Date | null = null;
    fieldsLabDateInvalid = false;
    fieldsLabDateTimeInvalid = false;
    fieldsLabTab: FieldsLabTab = 'inputs';
    readonly fieldsLabTabs: Array<{ value: FieldsLabTab; label: string; icon: string }> = [
      { value: 'inputs', label: 'Inputs', icon: 'text_fields' },
      { value: 'select', label: 'Select', icon: 'arrow_drop_down_circle' },
      { value: 'textarea', label: 'Textarea', icon: 'notes' },
      { value: 'date', label: 'Data', icon: 'calendar_today' },
      { value: 'search', label: 'Pesquisa', icon: 'search' },
      { value: 'buttons', label: 'Botões', icon: 'smart_button' },
      { value: 'icons', label: 'Ícones', icon: 'dashboard_customize' },
      { value: 'deploy', label: 'Deploy', icon: 'rocket_launch' }
    ];
    fieldsLabSelectOptions = [
      { value: 'opcao_1', label: 'Opção 1' },
      { value: 'opcao_2', label: 'Opção 2' },
      { value: 'opcao_3', label: 'Opção 3' }
    ];
    readonly fieldsLabIconCatalog = [
      'add', 'edit', 'delete', 'close', 'search', 'filter_list', 'tune', 'save', 'check', 'check_circle',
      'cancel', 'warning', 'info', 'error_outline', 'calendar_today', 'calendar_month', 'event', 'event_note',
      'history', 'dashboard', 'dashboard_customize', 'grid_view', 'home', 'cloud', 'forum', 'chat', 'mail',
      'campaign', 'rss_feed', 'description', 'picture_as_pdf', 'folder', 'folder_open', 'insert_drive_file',
      'assignment', 'fact_check', 'task_alt', 'verified_user', 'security', 'lock', 'lock_reset', 'badge',
      'person', 'person_add', 'groups', 'group_work', 'business', 'business_center', 'domain', 'place',
      'location_on', 'pin_drop', 'work_outline', 'support_agent', 'quiz', 'rate_review', 'insights',
      'query_stats', 'timeline', 'donut_large', 'palette', 'image', 'photo', 'add_photo_alternate',
      'upload_file', 'attach_file', 'content_copy', 'open_in_new', 'reply', 'more_vert', 'more_horiz',
      'expand_more', 'chevron_left', 'chevron_right', 'keyboard_arrow_down', 'keyboard_arrow_up',
      'favorite', 'sentiment_satisfied_alt', 'wb_sunny', 'hourglass_bottom', 'play_circle', 'mic', 'videocam'
    ];
    readonly fieldsLabSelectAllValue = '__ALL__';
    fieldsLabFileName = '';
    taskForm: FormGroup;
    draftSubtasks: string[] = [];
    groups: any[] = [];
    tags: any[] = [];
    members: any[] = [];
    subgroups: any[] = [];
    buckets: any[] = [];
    availableFormSubgroups: any[] = [];
    availableFormBuckets: any[] = [];
    inlineBucketLoading = false;
    private tenantId: number | null = null;
    private userId: number | null = null;
    private userDepartmentId: number | null = null;
    private taskDataLoaded = false;
    private weatherIntervalId: number | null = null;
    private controlhelpIntervalId: number | null = null;
    private lastAccessAt: string | null = null;
    private readonly moduleKeys = [
      'home',
      'intranet',
      'cadastros',
      'controlsign',
      'controltask',
      'controlproject',
      'controlflow',
      'controlhelp',
      'controlbordo',
      'controlchat',
      'controlcli',
      'controldocs',
      'controlsafe',
      'controlmentor',
      'controlquestion',
      'controlai',
      'controlsocial',
      'controldp',
      'controlop',
      'controlinventory',
      'mystore',
      'controldossie'
    ];
    private readonly moduleSearchCatalog: ModuleSearchEntry[] = [
      { key: 'home', label: 'Home', route: '/feed', icon: 'home', aliases: ['inicio', 'início', 'feed', 'pagina inicial'] },
      { key: 'intranet', label: 'Intranet', route: '/feed', icon: 'cloud', aliases: ['feed', 'mural', 'social', 'intranet'] },
      { key: 'cadastros', label: 'Cadastros', route: '/cadastros', icon: 'assignment', aliases: ['cadastro', 'formularios', 'configuracoes'] },
      { key: 'controlsign', label: 'ControlSign', route: '/controlsign', icon: 'picture_as_pdf', aliases: ['assinatura', 'documento', 'pdf'] },
      { key: 'controltask', label: 'ControlTask', route: '/controltask', icon: 'check_circle', aliases: ['task', 'tarefas', 'atividade'] },
      { key: 'controlproject', label: 'ControlProject', route: '/controlproject', icon: 'work_outline', aliases: ['project', 'projeto', 'projetos'] },
      { key: 'controlflow', label: 'ControlFlow', route: '/controlflow', icon: 'schema', aliases: ['flow', 'fluxo', 'workflow'] },
      { key: 'controlhelp', label: 'ControlHelp', route: '/controlhelp', icon: 'support_agent', aliases: ['help', 'suporte', 'chamado'] },
      { key: 'controlbordo', label: 'Diário de Bordo', route: '/controlbordo', icon: 'note_alt', aliases: ['controlbordo', 'bordo', 'diario'] },
      { key: 'controlchat', label: 'ControlChat', route: '/controlchat-desktop', icon: 'chat', aliases: ['chat', 'mensagens', 'assistente'] },
      { key: 'controlcli', label: 'ControlCLI', route: '/controlcli', icon: 'shopping_bag', aliases: ['cli', 'clientes', 'vendas'] },
      { key: 'controldocs', label: 'ControlDocs', route: '/controldocs', icon: 'folder', aliases: ['docs', 'documentos', 'repositorio'] },
      { key: 'controlsafe', label: 'ControlSafe', route: '/controlsafe', icon: 'lock', aliases: ['safe', 'seguranca', 'segurança'] },
      { key: 'controlmentor', label: 'ControlMentor', route: '/controlmentor', icon: 'diversity_3', aliases: ['mentor', 'mentoria'] },
      { key: 'controlquestion', label: 'ControlQuestion', route: '/controlquestion', icon: 'quiz', aliases: ['question', 'pesquisa', 'questionario', 'questionário'] },
      { key: 'controlai', label: 'ControlAI', route: '/controlai', icon: 'smart_toy', aliases: ['ai', 'ia', 'assistente virtual'] },
      { key: 'controlsocial', label: 'ControlSocial', route: '/controlsocial', icon: 'forum', aliases: ['social', 'comunicacao', 'comunicação', 'postagens'] },
      { key: 'controldp', label: 'ControlDP', route: '/controldp', icon: 'badge', aliases: ['dp', 'admissao', 'admissão', 'pessoal'] },
      { key: 'controlop', label: 'ControlOP', route: '/controlop', icon: 'settings_suggest', aliases: ['op', 'operacao', 'operação'] },
      { key: 'controlinventory', label: 'ControlInventory', route: '/controlinventory', icon: 'inventory_2', aliases: ['inventario', 'inventário', 'ativos', 'kits', 'patrimonio', 'patrimônio'] },
      { key: 'mystore', label: 'My Store', route: '/mystore', icon: 'storefront', aliases: ['store', 'loja', 'estoque', 'vendas', 'caixa'] },
      { key: 'controldossie', label: 'ControlDossie', route: '/controldossie', icon: 'folder_special', aliases: ['dossie', 'dossiê', 'evidencias', 'evidências', 'boletim'] }
    ];
    private sub: Subscription | null = null;
    private languageSub: Subscription | null = null;
    private routerEventsSub: Subscription | null = null;
    @ViewChild('menuListRef') menuListRef?: ElementRef<HTMLUListElement>;
    private readonly menuScrollStorageKey = 'controlps.menu.scrollTop';

  constructor(
    private userSession: UserSessionService,
    private fb: FormBuilder,
    private http: HttpClient,
    private controltaskService: ControltaskService,
    private controlhelpService: ControlhelpService,
    private auth: AuthService,
    private logService: LogService,
    private router: Router,
    private languageService: LanguageService
  ) {
    this.taskForm = this.fb.group({
      title: ['', Validators.required],
      description: [''],
      priority: ['MEDIUM'],
      visibility: ['PRIVATE'],
      status: ['OPEN'],
      start_date: [''],
      due_date: [''],
      subgroupId: [''],
      bucketId: [''],
      groupId: [''],
      tagIds: [[]],
      assigneeId: [''],
      newSubtask: [''],
      inlineBucketName: [''],
      inlineBucketDescription: ['']
    });
  }

  ngOnInit() {
    this.updateViewportState();
    document.body.classList.add('has-fixed-menu');
    document.body.classList.add('has-topbar');
    this.syncBodyState();
    this.intranetOpen = false;
    this.languageLabel = this.languageService.current().toUpperCase();
    this.languageSub = this.languageService.changes().subscribe((lang) => {
      this.languageLabel = String(lang || 'pt').toUpperCase();
    });
    this.sub = this.userSession.user$.subscribe(user => this.updateInfo(user));
    this.updateInfo(this.userSession.getUser());
    this.routerEventsSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd && this.mobileMenuOpen) {
        this.closeMobileMenu();
      }
    });
    this.loadWeather();
    this.weatherIntervalId = window.setInterval(() => this.loadWeather(), 30 * 60 * 1000);
  }

  ngAfterViewInit() {
    window.requestAnimationFrame(() => this.restoreMenuScrollPosition());
  }

  ngOnDestroy() {
    this.rememberMenuScrollPosition();
    this.sub?.unsubscribe();
    this.languageSub?.unsubscribe();
    this.routerEventsSub?.unsubscribe();
    if (this.weatherIntervalId !== null) {
      window.clearInterval(this.weatherIntervalId);
    }
    if (this.controlhelpIntervalId !== null) {
      window.clearInterval(this.controlhelpIntervalId);
    }
    document.body.classList.remove('has-fixed-menu');
    document.body.classList.remove('menu-closed');
    document.body.classList.remove('has-topbar');
    document.body.classList.remove('cp-modal-open');
  }

  toggleLanguage() {
    this.languageService.toggle();
  }

  toggleMenu() {
    this.openedByHover = false;
    this.isClosed = !this.isClosed;
    this.intranetOpen = false;
    this.syncBodyState();
  }

  onMenuMouseEnter() {
    if (this.isMobileViewport) {
      return;
    }
    if (!this.isClosed) {
      return;
    }
    this.openedByHover = true;
    this.isClosed = false;
    this.syncBodyState();
    window.requestAnimationFrame(() => this.restoreMenuScrollPosition());
  }

  onMenuMouseLeave() {
    if (this.isMobileViewport) {
      return;
    }
    if (!this.openedByHover) {
      return;
    }
    this.rememberMenuScrollPosition();
    this.openedByHover = false;
    this.isClosed = true;
    this.intranetOpen = false;
    this.syncBodyState();
  }

  onMenuListScroll() {
    this.rememberMenuScrollPosition();
  }

  toggleMobileMenu() {
    if (!this.isMobileViewport) {
      return;
    }
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
      return;
    }
    this.mobileMenuOpen = true;
    this.openedByHover = false;
    this.isClosed = false;
    this.syncBodyState();
    window.requestAnimationFrame(() => this.restoreMenuScrollPosition());
  }

  closeMobileMenu() {
    if (!this.mobileMenuOpen) {
      return;
    }
    this.rememberMenuScrollPosition();
    this.mobileMenuOpen = false;
    this.isClosed = true;
    this.intranetOpen = false;
    this.openedByHover = false;
    this.syncBodyState();
  }

  toggleIntranet(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.isClosed) {
      this.isClosed = false;
      this.syncBodyState();
      this.intranetOpen = true;
      return;
    }
    this.intranetOpen = !this.intranetOpen;
  }

  private updateInfo(user: any) {
    if (!user) {
      this.userInfo = null;
      this.allowedModules.clear();
      this.modulePermissions.clear();
      this.hasModuleList = false;
      this.menuScrollable = false;
      this.moduleSearchTerm = '';
      this.moduleSearchResults = [];
      this.moduleSearchOpen = false;
      this.tenantId = null;
      this.userId = null;
      this.userDepartmentId = null;
      this.todayTasksCount = 0;
      this.controlhelpHasUpdates = false;
      this.controlhelpDepartmentIds = [];
      this.lastAccessAt = null;
      this.isMasterOnlyProfile = false;
      if (this.controlhelpIntervalId !== null) {
        window.clearInterval(this.controlhelpIntervalId);
        this.controlhelpIntervalId = null;
      }
      return;
    }

    const previousTenantId = this.tenantId;
    this.tenantId = user?.tenant_id ?? user?.tenant?.id ?? null;
    this.isMainTenant = Number(this.tenantId) === 1;
    this.userId = user?.id ?? user?.user_id ?? null;
    this.userDepartmentId = user?.department_id ?? user?.department?.id ?? null;
    this.controlhelpDepartmentIds = Array.isArray(user?.controlhelp_departments)
      ? user.controlhelp_departments.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id))
      : [];
    this.lastAccessAt = user?.last_access_at ?? null;
    if (previousTenantId !== this.tenantId) {
      this.taskDataLoaded = false;
      this.groups = [];
      this.tags = [];
      this.members = [];
      this.subgroups = [];
      this.buckets = [];
    }

    const tenantName = user?.tenant_name || user?.tenant?.name || '—';
    const roleName = user?.role_name || user?.role?.name || '—';
    const roleNameLower = String(roleName || '').toLowerCase();
    const lastAccessLabel = user?.last_access_at
      ? this.formatDate(user.last_access_at)
      : 'Primeiro acesso';
    const departmentName = user?.department_name || user?.department?.name || '—';
    const displayName =
      user?.name || user?.full_name || user?.username || user?.email || 'Usuário';
    const initials = displayName
      .split(' ')
      .filter((part: string) => part.trim().length)
      .slice(0, 2)
      .map((part: string) => part[0])
      .join('')
      .toUpperCase();

    this.userInfo = {
      tenantName,
      lastAccessLabel,
      roleName,
      departmentName,
      displayName,
      initials: initials || 'U'
    };
    this.isMasterProfile =
     roleNameLower.includes('master') ||
      roleNameLower === 'admin';
    this.isMasterOnlyProfile = roleNameLower.includes('master');
    const isAdmin = String(user?.role_name || user?.role?.name || '').toLowerCase() === 'admin';
    if (isAdmin) {
      this.hasModuleList = false;
      this.allowedModules = new Set(this.moduleKeys);
      this.modulePermissions = new Map(
        this.moduleKeys.map((key) => [key, { can_view: true, can_create: true, can_manage: true }])
      );
      this.menuScrollable = this.getEnabledModuleCount() >= 8;
      this.refreshModuleSearchResults();
      return;
    }

    if (Array.isArray(user.modules)) {
      this.hasModuleList = true;
      this.modulePermissions = new Map(
        user.modules.map((module: any) => [
          module.module_key,
          {
            can_view: !!module.can_view,
            can_create: !!module.can_create,
            can_manage: !!module.can_manage
          }
        ])
      );
      this.allowedModules = new Set(
        user.modules
          .filter((module: any) => module.can_view)
          .map((module: any) => module.module_key)
      );
    } else {
      this.hasModuleList = false;
      this.allowedModules.clear();
      this.modulePermissions.clear();
    }

    this.menuScrollable = this.getEnabledModuleCount() >= 8;
    this.refreshModuleSearchResults();
    if (this.canViewModule('controltask')) {
      this.loadTaskSupportData();
      this.refreshTodayTasks();
    } else {
      this.todayTasksCount = 0;
    }

    if (this.canViewModule('controlhelp')) {
      this.refreshControlhelpUpdates();
      if (this.controlhelpIntervalId === null) {
        this.controlhelpIntervalId = window.setInterval(() => this.refreshControlhelpUpdates(), 60000);
      }
    } else {
      this.controlhelpHasUpdates = false;
      if (this.controlhelpIntervalId !== null) {
        window.clearInterval(this.controlhelpIntervalId);
        this.controlhelpIntervalId = null;
      }
    }
  }

  canViewModule(moduleKey: string) {
    if (this.isMasterProfile) {
      return true;
    }
    if (!this.hasModuleList) {
      return true;
    }
    return this.allowedModules.has(moduleKey);
  }

  openMonitoringPage() {
    if (!this.isMainTenant) {
      return;
    }
    this.router.navigate(['/controlmonitor']);
    if (this.mobileMenuOpen) {
      this.closeMobileMenu();
    }
  }

  canCreateOrManage(moduleKey: string) {
    if (this.isMasterProfile) {
      return true;
    }
    if (!this.hasModuleList) {
      return true;
    }
    const perms = this.modulePermissions.get(moduleKey);
    return !!(perms?.can_create || perms?.can_manage);
  }

  onModuleSearchInput(value: string) {
    this.moduleSearchTerm = value || '';
    this.moduleSearchOpen = true;
    this.refreshModuleSearchResults();
  }

  onModuleSearchFocus() {
    this.moduleSearchOpen = true;
    this.refreshModuleSearchResults();
  }

  selectModuleResult(item: ModuleSearchEntry) {
    if (!item?.route) {
      return;
    }
    this.moduleSearchTerm = '';
    this.moduleSearchOpen = false;
    this.refreshModuleSearchResults();
    this.router.navigate([item.route]);
  }

  private syncBodyState() {
    document.body.classList.toggle('menu-closed', this.isClosed);
  }

  private updateViewportState() {
    const isMobile = window.innerWidth <= 992;
    if (!isMobile && this.mobileMenuOpen) {
      this.mobileMenuOpen = false;
    }
    this.isMobileViewport = isMobile;
  }

  private rememberMenuScrollPosition() {
    const menuList = this.menuListRef?.nativeElement;
    if (!menuList) {
      return;
    }
    const scrollTop = Math.max(0, menuList.scrollTop || 0);
    try {
      sessionStorage.setItem(this.menuScrollStorageKey, String(scrollTop));
    } catch {
      // Ignore session storage failures.
    }
  }

  private restoreMenuScrollPosition() {
    const menuList = this.menuListRef?.nativeElement;
    if (!menuList) {
      return;
    }
    let savedTop = 0;
    try {
      const stored = sessionStorage.getItem(this.menuScrollStorageKey);
      const parsed = stored !== null ? Number(stored) : 0;
      if (Number.isFinite(parsed) && parsed >= 0) {
        savedTop = parsed;
      }
    } catch {
      savedTop = 0;
    }
    menuList.scrollTop = savedTop;
  }

  private getEnabledModuleCount(): number {
    return this.moduleKeys.filter((moduleKey) => this.canViewModule(moduleKey)).length;
  }

  private formatDate(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }

    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(date);
  }

  private refreshModuleSearchResults() {
    const enabledModules = this.moduleSearchCatalog.filter((module) => this.canViewModule(module.key));
    const query = this.normalizeSearch(this.moduleSearchTerm);
    if (!query) {
      this.moduleSearchResults = enabledModules.slice(0, 12);
      return;
    }

    const ranked = enabledModules
      .map((module) => {
        const targets = [module.label, module.key, ...module.aliases].map((value) => this.normalizeSearch(value));
        const starts = targets.some((target) => target.startsWith(query));
        const contains = starts || targets.some((target) => target.includes(query));
        if (!contains) {
          return null;
        }
        return { module, score: starts ? 0 : 1 };
      })
      .filter((entry): entry is { module: ModuleSearchEntry; score: number } => !!entry)
      .sort((a, b) => (a.score - b.score) || a.module.label.localeCompare(b.module.label, 'pt-BR'))
      .map((entry) => entry.module);

    this.moduleSearchResults = ranked.slice(0, 20);
  }

  private normalizeSearch(value: string): string {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  openTaskModal() {
    if (!this.canViewModule('controltask')) {
      return;
    }
    if (!this.taskDataLoaded) {
      this.loadTaskSupportData();
    }
    this.taskForm.reset({
      title: '',
      description: '',
      priority: 'MEDIUM',
      visibility: 'PRIVATE',
      status: 'OPEN',
      start_date: '',
      due_date: '',
      subgroupId: '',
      bucketId: '',
      groupId: '',
      tagIds: [],
      assigneeId: '',
      newSubtask: '',
      inlineBucketName: '',
      inlineBucketDescription: ''
    });
    this.handleGroupSelectionChange();
    this.handleSubgroupSelectionChange();
    this.draftSubtasks = [];
    this.showTaskModal = true;
    this.syncModalBodyState();
  }

  openBordoEntry() {
    if (!this.canViewModule('controlbordo')) {
      return;
    }
    this.router.navigate(['/controlbordo'], { queryParams: { newEntry: '1' } });
  }

  closeTaskModal() {
    this.showTaskModal = false;
    this.syncModalBodyState();
  }

  openFieldsLabModal() {
    if (!this.isMasterProfile) {
      return;
    }
    this.closeAvatarMenu();
    this.showFieldsLabModal = true;
    this.fieldsLabTab = 'inputs';
    this.syncModalBodyState();
  }

  closeFieldsLabModal() {
    this.showFieldsLabModal = false;
    this.fieldsLabTab = 'inputs';
    this.syncModalBodyState();
  }

  setFieldsLabTab(tab: FieldsLabTab) {
    this.fieldsLabTab = tab;
  }

  onFieldsLabMultiSelectChange(values: string[]) {
    const normalized = Array.isArray(values) ? values : [];
    const selected = normalized.filter((value) => value !== this.fieldsLabSelectAllValue);
    const hasAll = normalized.includes(this.fieldsLabSelectAllValue);
    const allOptionValues = this.fieldsLabSelectOptions.map((option) => option.value);
    const hasEveryOption = allOptionValues.every((value) => selected.includes(value));

    if (hasAll && !hasEveryOption) {
      this.fieldsLabForm.multiSelectValues = [...allOptionValues];
      return;
    }

    if (hasAll && hasEveryOption) {
      this.fieldsLabForm.multiSelectValues = [];
      return;
    }

    this.fieldsLabForm.multiSelectValues = selected;
  }

  onFieldsLabDateInput(rawValue: string) {
    const digits = String(rawValue || '').replace(/\D/g, '').slice(0, 8);
    let formatted = digits;
    if (digits.length > 4) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    } else if (digits.length > 2) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    this.fieldsLabForm.dateValue = formatted;
    this.fieldsLabDatePickerValue = this.parseBrDate(formatted);
    this.fieldsLabDateInvalid = formatted.length === 10 && !this.fieldsLabDatePickerValue;
  }

  onFieldsLabDateBlur() {
    const normalized = this.fieldsLabForm.dateValue.trim();
    if (!normalized) {
      this.fieldsLabDatePickerValue = null;
      this.fieldsLabDateInvalid = false;
      return;
    }
    const parsed = this.parseBrDate(normalized);
    if (parsed) {
      this.fieldsLabDatePickerValue = parsed;
      this.fieldsLabForm.dateValue = this.formatBrDate(parsed);
      this.fieldsLabDateInvalid = false;
      return;
    }
    this.fieldsLabDateInvalid = true;
  }

  onFieldsLabDatePicked(event: any) {
    const picked = event?.value instanceof Date ? event.value : null;
    if (!picked || Number.isNaN(picked.getTime())) {
      return;
    }
    this.fieldsLabDatePickerValue = picked;
    this.fieldsLabForm.dateValue = this.formatBrDate(picked);
    this.fieldsLabDateInvalid = false;
  }

  onFieldsLabDateTimeInput(rawValue: string) {
    const digits = String(rawValue || '').replace(/\D/g, '').slice(0, 12);
    let formatted = digits;
    if (digits.length > 10) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}, ${digits.slice(8, 10)}:${digits.slice(10, 12)}`;
    } else if (digits.length > 8) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}, ${digits.slice(8)}`;
    } else if (digits.length > 4) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    } else if (digits.length > 2) {
      formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }
    this.fieldsLabForm.dateTimeValue = formatted;

    const parsed = this.parseBrDateTime(formatted);
    if (parsed) {
      this.fieldsLabDateTimePickerValue = parsed;
      this.fieldsLabDateTimeInvalid = false;
      return;
    }

    const datePart = this.extractDatePart(formatted);
    this.fieldsLabDateTimePickerValue = datePart ? this.parseBrDate(datePart) : null;
    this.fieldsLabDateTimeInvalid = this.isPotentiallyCompleteDateTime(formatted);
  }

  onFieldsLabDateTimeBlur() {
    const normalized = this.fieldsLabForm.dateTimeValue.trim();
    if (!normalized) {
      this.fieldsLabDateTimePickerValue = null;
      this.fieldsLabDateTimeInvalid = false;
      return;
    }

    const parsed = this.parseBrDateTime(normalized);
    if (parsed) {
      this.fieldsLabDateTimePickerValue = parsed;
      this.fieldsLabForm.dateTimeValue = this.formatBrDateTime(parsed);
      this.fieldsLabDateTimeInvalid = false;
      return;
    }
    this.fieldsLabDateTimeInvalid = true;
  }

  onFieldsLabDateTimePicked(event: any) {
    const picked = event?.value instanceof Date ? event.value : null;
    if (!picked || Number.isNaN(picked.getTime())) {
      return;
    }

    const currentTime = this.extractTimePart(this.fieldsLabForm.dateTimeValue);
    const hours = currentTime?.hours ?? 0;
    const minutes = currentTime?.minutes ?? 0;
    const merged = new Date(
      picked.getFullYear(),
      picked.getMonth(),
      picked.getDate(),
      hours,
      minutes,
      0,
      0
    );

    this.fieldsLabDateTimePickerValue = merged;
    this.fieldsLabForm.dateTimeValue = this.formatBrDateTime(merged);
    this.fieldsLabDateTimeInvalid = false;
  }

  onFieldsLabFileChange(event: Event) {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0] ?? null;
    this.fieldsLabFileName = file?.name || '';
  }

  onFieldsLabDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onFieldsLabDrop(event: DragEvent) {
    event.preventDefault();
    const file = event.dataTransfer?.files?.[0] ?? null;
    this.fieldsLabFileName = file?.name || '';
  }

  addDraftSubtask() {
    const text = (this.taskForm.get('newSubtask')?.value || '').trim();
    if (!text) {
      return;
    }
    this.draftSubtasks.push(text);
    this.taskForm.get('newSubtask')?.reset('');
  }

  removeDraftSubtask(index: number) {
    this.draftSubtasks.splice(index, 1);
  }

  handleGroupSelectionChange() {
    const selectedGroup = Number(this.taskForm.get('groupId')?.value) || null;
    if (!selectedGroup) {
      this.availableFormSubgroups = [];
      this.availableFormBuckets = [];
      this.taskForm.patchValue({ subgroupId: '', bucketId: '', inlineBucketName: '', inlineBucketDescription: '' });
      return;
    }
    this.availableFormSubgroups = this.subgroups.filter((subgroup) => subgroup.group_id === selectedGroup);
    const currentSubgroup = Number(this.taskForm.get('subgroupId')?.value) || null;
    if (!currentSubgroup || !this.availableFormSubgroups.some((subgroup) => subgroup.id === currentSubgroup)) {
      this.taskForm.patchValue({ subgroupId: '', bucketId: '', inlineBucketName: '', inlineBucketDescription: '' });
    }
    this.handleSubgroupSelectionChange();
  }

  handleSubgroupSelectionChange() {
    const subgroupId = Number(this.taskForm.get('subgroupId')?.value) || null;
    if (!subgroupId) {
      this.availableFormBuckets = [];
      this.taskForm.patchValue({ bucketId: '', inlineBucketName: '', inlineBucketDescription: '' });
      return;
    }
    this.availableFormBuckets = this.buckets.filter((bucket) => bucket.subgroup_id === subgroupId);
    const currentBucket = Number(this.taskForm.get('bucketId')?.value) || null;
    if (currentBucket && !this.availableFormBuckets.some((bucket) => bucket.id === currentBucket)) {
      this.taskForm.patchValue({ bucketId: '' });
    }
  }

  createInlineBucket() {
    if (!this.tenantId || this.inlineBucketLoading) {
      return;
    }
    const subgroupId = Number(this.taskForm.get('subgroupId')?.value) || null;
    const groupId = Number(this.taskForm.get('groupId')?.value) || null;
    const bucketName = (this.taskForm.get('inlineBucketName')?.value || '').trim();
    const description = (this.taskForm.get('inlineBucketDescription')?.value || '').trim();
    if (!subgroupId || !groupId || !bucketName) {
      return;
    }
    this.inlineBucketLoading = true;
    const payload = {
      tenant_id: this.tenantId,
      group_id: groupId,
      subgroup_id: subgroupId,
      name: bucketName,
      description: description || null
    };
    this.controltaskService.createBucket(payload).subscribe({
      next: (bucket: any) => {
        this.buckets = [...this.buckets, bucket];
        this.availableFormBuckets = this.buckets.filter((item) => item.subgroup_id === subgroupId);
        this.taskForm.patchValue({ bucketId: bucket.id, inlineBucketName: '', inlineBucketDescription: '' });
        this.inlineBucketLoading = false;
      },
      error: () => {
        this.inlineBucketLoading = false;
      }
    });
  }

  submitTask() {
    if (!this.taskForm.valid || !this.tenantId || !this.userId) {
      return;
    }
    const value = this.taskForm.value;
    const payload = {
      tenant_id: this.tenantId,
      created_by: this.userId,
      title: value.title,
      description: value.description || null,
      priority: value.priority,
      visibility: value.visibility,
      status: value.status,
      due_date: value.due_date || null,
      start_date: value.start_date || null,
      group_id: value.groupId ? Number(value.groupId) : null,
      subgroup_id: value.subgroupId ? Number(value.subgroupId) : null,
      bucket_id: value.bucketId ? Number(value.bucketId) : null,
      tag_ids: value.tagIds || [],
      assignee_id: value.assigneeId ? Number(value.assigneeId) : null,
      subtasks: this.draftSubtasks,
      task_code: this.generateTaskCode()
    };
    this.controltaskService.createTask(payload).subscribe({
      next: () => {
        this.showTaskModal = false;
        this.refreshTodayTasks();
      }
    });
  }

  private loadTaskSupportData() {
    if (!this.tenantId || this.taskDataLoaded) {
      return;
    }
    this.taskDataLoaded = true;
    this.controltaskService.getGroups(this.tenantId, {
      user_id: this.userId,
      department_id: this.userDepartmentId
    }).subscribe({
      next: (groups) => (this.groups = groups || []),
      error: () => (this.groups = [])
    });
    this.controltaskService.getTags(this.tenantId).subscribe({
      next: (tags) => (this.tags = tags || []),
      error: () => (this.tags = [])
    });
    this.controltaskService.getUsers(this.tenantId).subscribe({
      next: (users) => (this.members = users || []),
      error: () => (this.members = [])
    });
    this.controltaskService.getSubgroups(this.tenantId, undefined, {
      user_id: this.userId,
      department_id: this.userDepartmentId
    }).subscribe({
      next: (subgroups) => (this.subgroups = subgroups || []),
      error: () => (this.subgroups = [])
    });
    this.controltaskService.getBuckets(this.tenantId).subscribe({
      next: (buckets) => (this.buckets = buckets || []),
      error: () => (this.buckets = [])
    });
  }

  private refreshTodayTasks() {
    if (!this.tenantId) {
      this.todayTasksCount = 0;
      return;
    }
    const todayKey = this.dateKey(new Date(), 'America/Sao_Paulo');
    if (!todayKey) {
      this.todayTasksCount = 0;
      return;
    }
    const filter: Record<string, any> = { start_date: todayKey };
    if (this.userId) {
      filter['user_id'] = this.userId;
    }
    if (this.userDepartmentId) {
      filter['department_id'] = this.userDepartmentId;
    }
    this.controltaskService.getTasks(this.tenantId, filter).subscribe({
      next: (tasks) => {
        this.todayTasksCount = tasks?.length || 0;
      },
      error: () => {
        this.todayTasksCount = 0;
      }
    });
  }

  private loadWeather() {
    const url =
      'https://api.open-meteo.com/v1/forecast?latitude=-23.55&longitude=-46.63&current=temperature_2m,weather_code&daily=temperature_2m_max,temperature_2m_min&timezone=America%2FSao_Paulo';
    this.http.get<any>(url).subscribe({
      next: (data) => {
        const currentTemp = data?.current?.temperature_2m;
        const minTemp = data?.daily?.temperature_2m_min?.[0];
        const maxTemp = data?.daily?.temperature_2m_max?.[0];
        const weatherCode = data?.current?.weather_code;
        this.weatherTempLabel = Number.isFinite(currentTemp) ? `${Math.round(currentTemp)}°C` : '--°C';
        if (Number.isFinite(minTemp) && Number.isFinite(maxTemp)) {
          this.weatherRangeLabel = `Hoje: ${Math.round(maxTemp)}°C / ${Math.round(minTemp)}°C`;
        } else {
          this.weatherRangeLabel = 'Hoje: --°C / --°C';
        }
        this.weatherIcon = this.mapWeatherIcon(weatherCode);
      },
      error: () => {
        this.weatherTempLabel = '--°C';
        this.weatherRangeLabel = 'Hoje: --°C / --°C';
        this.weatherIcon = 'cloud';
      }
    });
  }

  private mapWeatherIcon(code?: number): string {
    if (code === undefined || code === null) {
      return 'cloud';
    }
    if (code === 0) {
      return 'wb_sunny';
    }
    if (code === 1 || code === 2 || code === 3) {
      return 'cloud';
    }
    if (code === 45 || code === 48) {
      return 'filter_drama';
    }
    if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) {
      return 'grain';
    }
    if (code >= 71 && code <= 77) {
      return 'ac_unit';
    }
    if (code >= 95) {
      return 'bolt';
    }
    return 'cloud';
  }

  private dateKey(value?: unknown, timeZone = 'America/Sao_Paulo'): string | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) {
        return null;
      }
      return this.toDateKey(value, timeZone);
    }
    const normalized = String(value);
    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(normalized);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return this.toDateKey(parsed, timeZone);
  }

  private toDateKey(date: Date, timeZone: string): string {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    return formatter.format(date);
  }

  private generateTaskCode() {
    const seed = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += seed[Math.floor(Math.random() * seed.length)];
    }
    return result;
  }

  private parseBrDate(value: string): Date | null {
    const match = String(value || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!match) {
      return null;
    }
    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    if (!day || !month || month < 1 || month > 12 || year < 1000) {
      return null;
    }
    const candidate = new Date(year, month - 1, day);
    if (
      candidate.getFullYear() !== year ||
      candidate.getMonth() !== month - 1 ||
      candidate.getDate() !== day
    ) {
      return null;
    }
    return candidate;
  }

  private formatBrDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private parseBrDateTime(value: string): Date | null {
    const match = String(value || '').match(/^(\d{2})\/(\d{2})\/(\d{4})(?:,\s*|\s+)(\d{2}):(\d{2})$/);
    if (!match) {
      return null;
    }
    const [, day, month, year, hour, minute] = match;
    const parsedDate = this.parseBrDate(`${day}/${month}/${year}`);
    if (!parsedDate) {
      return null;
    }
    const hourNumber = Number(hour);
    const minuteNumber = Number(minute);
    if (
      Number.isNaN(hourNumber) ||
      Number.isNaN(minuteNumber) ||
      hourNumber < 0 ||
      hourNumber > 23 ||
      minuteNumber < 0 ||
      minuteNumber > 59
    ) {
      return null;
    }
    parsedDate.setHours(hourNumber, minuteNumber, 0, 0);
    return parsedDate;
  }

  private formatBrDateTime(date: Date): string {
    const datePart = this.formatBrDate(date);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${datePart}, ${hours}:${minutes}`;
  }

  private extractDatePart(value: string): string | null {
    const match = String(value || '').match(/^(\d{2}\/\d{2}\/\d{4})/);
    return match ? match[1] : null;
  }

  private extractTimePart(value: string): { hours: number; minutes: number } | null {
    const match = String(value || '').match(/(\d{2}):(\d{2})$/);
    if (!match) {
      return null;
    }
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (
      Number.isNaN(hours) ||
      Number.isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }
    return { hours, minutes };
  }

  private isPotentiallyCompleteDateTime(value: string): boolean {
    const normalized = String(value || '').trim();
    if (!normalized) {
      return false;
    }
    const digits = normalized.replace(/\D/g, '');
    return digits.length >= 12;
  }

  clearControlhelpUpdates() {
    this.controlhelpHasUpdates = false;
  }

  logout() {
    this.logService.register('LOGOUT_MANUAL', 'Usuário saiu do sistema').subscribe({
      error: () => {}
    });
    this.auth.logout();
    this.router.navigate(['/login'], { queryParams: { reason: 'logout' } });
  }

  toggleAvatarMenu(event: MouseEvent) {
    event.stopPropagation();
    this.avatarMenuOpen = !this.avatarMenuOpen;
    this.avatarMenuTarget = event.currentTarget as HTMLElement;
  }

  closeAvatarMenu() {
    this.avatarMenuOpen = false;
  }

  openDesktopDownload() {
    this.closeAvatarMenu();
    this.router.navigate(['/download-desktop']);
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    if (this.avatarMenuOpen) {
      if (!(this.avatarMenuTarget && this.avatarMenuTarget.contains(target))) {
        const menu = document.querySelector('.avatar-menu');
        if (!(menu && menu.contains(target))) {
          this.avatarMenuOpen = false;
        }
      }
    }

    if (this.moduleSearchOpen) {
      const searchRoot = this.moduleSearchRef?.nativeElement;
      if (!(searchRoot && searchRoot.contains(target))) {
        this.moduleSearchOpen = false;
      }
    }
  }

  @HostListener('document:keydown.escape')
  handleEscapeKey() {
    this.moduleSearchOpen = false;
    if (this.showFieldsLabModal) {
      this.closeFieldsLabModal();
    }
  }

  @HostListener('window:resize')
  onWindowResize() {
    const wasMobile = this.isMobileViewport;
    this.updateViewportState();
    if (wasMobile && !this.isMobileViewport) {
      this.closeMobileMenu();
    }
  }

  private refreshControlhelpUpdates() {
    if (!this.tenantId || !this.userId) {
      this.controlhelpHasUpdates = false;
      return;
    }
    const departmentScope = this.controlhelpDepartmentIds.length
      ? this.controlhelpDepartmentIds
      : this.isMasterProfile
        ? null
        : this.userDepartmentId
          ? [this.userDepartmentId]
          : [];
    if (Array.isArray(departmentScope) && !departmentScope.length) {
      this.controlhelpHasUpdates = false;
      return;
    }
    this.controlhelpService.getOpenCount(this.tenantId, this.userId, departmentScope).subscribe({
      next: (payload) => {
        this.controlhelpHasUpdates = Number(payload?.open_count || 0) > 0;
      },
      error: () => {
        this.controlhelpHasUpdates = false;
      }
    });
  }

  private syncModalBodyState() {
    const hasOpenModal = this.showTaskModal || this.showFieldsLabModal;
    document.body.classList.toggle('cp-modal-open', hasOpenModal);
  }

}
